import {
  BorrowingDetail,
  BorrowingDetailActions,
  BorrowingDetailMaterials,
} from "@/components/borrowing/detail/BorrowingDetail";
import IBorrowingData from "@/models/borrowing/BorrowingData";
import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CancelDialog } from "../../staff_history/components/dialog/CancelDialog";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import UserService from "@/lib/services/user/UserService";
import { IDetailBorrowing } from "@/models/borrowing/DetailBorrowing";
import { useMsal } from "@azure/msal-react";
import { RUANG_LAB_FOODTECH_ALSUT } from "@/lib/constants/constants";
import { AllocatedMaterials } from "../../borrowing/components/material/AllocatedMaterials";
import StockOpnameService from "@/lib/services/borrowing/StockOpnameService";

export interface StudentAllocation {
  batchId: number;
  brand: string;
  code: string;
  allocatedAmount: number;
}

export type FetchedMaterial = {
  id: number;
  name: string;
  requestAmount: number;
  unit: string;
  allocations: StudentAllocation[];
};

interface RawAllocation {
  batchId: number;
  allocatedAmount: number;
  batch: {
    brand: string;
    code: string;
  };
}

interface RawDetail {
  id: number;
  requestAmount: number;
  item: {
    name: string;
    sizeUnit: string;
  };
  allocations?: RawAllocation[];
}

const StudentHistoryDetail = () => {
  const params = useParams();
  const [data, setData] = useState<IBorrowingData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [materials, setMaterials] = useState<FetchedMaterial[]>([]);
  const { instance, accounts } = useMsal();
  const [materialsLoading, setMaterialsLoading] = useState<boolean>(false);

  const fetchData = async () => {
    const id = params.id;
    if (id) {
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_API_URL}borrowing/data`,
          {
            params: { id },
          }
        );
        console.log("fetched data:", res.data.data);

        res.data.data.detail_borrowings = await Promise.all(
          res.data.data.detail_borrowings.map(async (d: IDetailBorrowing) => ({
            id: d.id,
            borrowing_id: d.borrowing_id,
            student_nim: d.student_nim,
            student_name: await UserService.getName(d.student_nim),
          }))
        );
        res.data.data.title = "Borrowing Detail";
        setData(res.data.data);

        try {
          if (accounts && accounts.length > 0) {
            setMaterialsLoading(true);

            const tokenResponse = await instance.acquireTokenSilent({
              account: accounts[0],
              scopes: ["user.read"],
            });

            const materialRequests = await StockOpnameService.getStudentRequestByBorrowing(id, tokenResponse.accessToken);
            if (materialRequests) {
              
              const extractedMaterials: FetchedMaterial[] = materialRequests[0].details.map((d: RawDetail) => ({
                id: d.id,
                name: d.item.name,
                requestAmount: d.requestAmount,
                unit: d.item.sizeUnit,
                allocations: d.allocations?.map((a: RawAllocation) => ({
                  batchId: a.batchId,
                  brand: a.batch.brand,
                  code: a.batch.code,
                  allocatedAmount: a.allocatedAmount
                })) || []
              }));
              setMaterials(extractedMaterials);
            }
          }
        } catch (matError) {
          console.error("Failed to fetch materials:", matError);
        } finally {
          setMaterialsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [params.id]);

  const handleCancel = async (reason: string, reason_type: string) => {
    setLoading(true);
    try {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}borrowing/student/cancel`,
        { id: data?.id.toString(), reason: reason, reason_type: reason_type }
      );
      setLoading(false);
      fetchData();
    } catch (error) {
      console.error("Error cancelling borrowing:", error);
    }
  };

  return (
    <>
      <LoadingIndicator loading={loading} />
      <BorrowingDetail data={data}>
        {data?.room?.type?.id == RUANG_LAB_FOODTECH_ALSUT && (
          <>
            {data?.borrowing_status === "Pending" && (
              <BorrowingDetailMaterials 
                materials={materials} 
                isLoading={materialsLoading} 
              />
            )}

            {(data?.borrowing_status === "Accepted" || data?.borrowing_status === "Finalized") && (
              <AllocatedMaterials 
                materials={materials} 
                isLoading={materialsLoading} 
                hideBatchInfo={true}
                isFinalizedPresent={data?.borrowing_status === "Finalized" && data.attendance_status === "Present"}
              />
            )}
          </>
        )}

        {data?.borrowing_status === "Pending" && (
          <BorrowingDetailActions>
            <CancelDialog onCancel={handleCancel} />
          </BorrowingDetailActions>
        )}
      </BorrowingDetail>
    </>
  );
};

export default StudentHistoryDetail;
